'use client';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import type { Course as CourseType, Courseversion, ProfilePicture } from '@/payload/payload-types';
import { CartItem } from '../../_providers/Cart/reducer';
import { ContactUsForm } from '../../_components/Forms/ContactUsForm';
import Tick from '../../_icons/Tick';
import { Button } from '../Button';
import Plus from '../../_icons/Plus';
import { useCart } from '../../_providers/Cart';
import { useCourseVersion } from '../../_providers/CourseVersion';
import { Link } from '@/navigation';
import ChevronRight from '../../_icons/Chevron-right';
import { useScroll } from '../../_providers/ScrollProvider';
import { getDiscountReduction, useTrackEvent } from '../../_providers/TrackEventProvider';
import { logToPayload } from '../../_utilities/log-to-payload';

export const ContactSection: React.FC<{
	course: CourseType;
}> = ({ course }) => {
	const t = useTranslations('Course');
	const { cart, addItemToCart } = useCart();
	const { selectedCourseVersion } = useCourseVersion();
	const trackEvent = useTrackEvent();
	const constructedCartItem: CartItem | null = selectedCourseVersion
		? {
				orderedCollection: {
					value: selectedCourseVersion,
					relationTo: 'courseversions',
				},
				quantity: 1,
				id: selectedCourseVersion.id,
				selfParticipant: false,
			}
		: null;

	const [hasSubmittedForm, setHasSubmittedForm] = useState<boolean>(false);
	const [isCourseInCart, setIsCourseInCart] = useState<boolean>(false);

	useEffect(() => {
		const isItemAddedToCart = !!cart?.cartItems?.some(item => {
			if (
				typeof item.orderedCollection.value === 'string' ||
				typeof item.orderedCollection.value.course === 'string'
			) {
				logToPayload('Bad depth for item.orderedCollection.value at <ContactUsForm/>');

				return false;
			}
			if (typeof selectedCourseVersion?.course === 'string') {
				logToPayload('Bad depth for selectedCourseVersion?.course at <ContactUsForm/>');
				return false;
			}
			return item.orderedCollection.value?.course?.id === selectedCourseVersion?.course.id;
		});

		setIsCourseInCart(isItemAddedToCart);
	}, [cart]);

	const addCourseToCart = () => {
		setIsCourseInCart(true);
		if (constructedCartItem) {
			addItemToCart(constructedCartItem);
		}
	};

	const { contactSectionRef } = useScroll();
	if (!course.courseSalesRep) {
		return null;
	}

	if (typeof course.courseSalesRep === 'string') {
		logToPayload('Bad depth for course salesrep at <ContactSection/>:', course.title);
		return null;
	}
	if (typeof course.courseSalesRep.salesRepPicture === 'string') {
		logToPayload('Bad depth for course salesrep picture at <ContactSection/>:', course.title);
		return null;
	}
	const salesRepPicture = course.courseSalesRep.salesRepPicture as ProfilePicture;
	const salesRepImgUrl = salesRepPicture.sizes?.avatar?.url || salesRepPicture.url;

	if (!salesRepImgUrl) {
		logToPayload('ContactSection: salesRepImgUrl missing ', {
			salesRepPicture,
			courseId: course.id,
			courseversionId: selectedCourseVersion?.id,
		});
	}

	return (
		<section ref={contactSectionRef} className="bg-gold-light-8 px-4 py-[120px] lg:px-0">
			<div className="mx-auto lg:container">
				<div className="flex flex-col lg:max-w-[586px] lg:flex-1 lg:px-6 xl:max-w-[800px]">
					<div>
						<h3 className="text-muted-navy text-lg leading-10 font-bold">
							{t('contact-person-greeting-title')}
						</h3>
						<p className="text-muted-navy leading-8">
							{t.rich('contact-person-greeting', {
								b: chunks => <strong>{chunks}</strong>,
								courseTitle: course.title,
							})}
						</p>
					</div>
					<div>
						<div className="mt-4 mb-8 flex items-center">
							{salesRepImgUrl && (
								<Image
									className="mr-4 rounded-full"
									height={64}
									quality={100}
									alt={course.courseSalesRep.salesRepTitle || ''}
									src={salesRepImgUrl}
									width={64}
								/>
							)}
							<div className="flex flex-col">
								<p className="text-muted-navy font-bold">
									{course.courseSalesRep.name}
								</p>
								<p className="text-gold-light text-sm">
									{course.courseSalesRep.salesRepTitle}
								</p>
							</div>
						</div>

						<div className={hasSubmittedForm ? 'pointer-events-none opacity-50' : ''}>
							<ContactUsForm
								selectedCourseVersion={selectedCourseVersion}
								isCourseInCart={isCourseInCart}
								addCourseToCart={addCourseToCart}
								hasSubmittedForm={hasSubmittedForm}
								onFormSubmit={() => setHasSubmittedForm(true)}
								course={course}
							/>
						</div>
					</div>
					<div className={`${hasSubmittedForm ? 'flex flex-col space-y-8' : 'hidden'}`}>
						<div className="bg-success flex w-fit items-center space-x-4 rounded-lg p-6 font-bold text-white">
							<div className="flex h-4 w-4 items-center">
								<Tick color="white" />
							</div>
							<p>
								{t('contact-form-feedback', {
									salesRepName: course.courseSalesRep.name,
								})}
							</p>
						</div>
						{isCourseInCart ? (
							<Link
								className="group text-brand-navy flex w-fit items-center space-x-6 px-4 pb-4 text-sm lg:space-x-4 lg:text-base"
								href="/cart"
								onClick={() =>
									trackEvent('go_to_cart_clicked', {
										component: 'contact_us_section',
									})
								}
							>
								<div className="flex flex-col lg:flex-row">
									<p>{t('course-is-in-cart')}</p>

									<p className="font-bold group-hover:underline lg:ml-1">
										{t('continue-to-payment')}
									</p>
								</div>
								<ChevronRight color="#041e41" />
							</Link>
						) : (
							<Button
								onClick={() => {
									addCourseToCart();
									trackEvent('add_to_cart_clicked', {
										course_name: course.title,
										component: 'contact_us_section',
										course_discount_value_eur:
											getDiscountReduction(selectedCourseVersion),
									});
								}}
								size="small"
								variant="outlined"
								intent="primary"
							>
								{t('buy-course-now')}
								<Plus color="#041E41" className="size-4" />
							</Button>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};
